import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { delay } from "../../helper/delayresponse";

interface SmsCampaign {
    id?: number;
    name: string;
    agent_sending_limit: string;
    createdAt?: string;
    updatedAt?: string;
    sms_campaign_numbers: {
        sms_gateway_id: number,
        sms_gateway_number_id: number,
    }[],
    sms_campaign_users: number
}

interface SmsCampaignState {
    smscampaigndata: SmsCampaign[];
    info: { count: number; current_page: number; limit: number; total_page: number } | null;
    error: string | null;
    loading: boolean;
}

// ====================-SmsCampgain-listing-action-start==============
export const GetallSmsCampaign = createAsyncThunk<
    { data: SmsCampaign[], info: { count: number; current_page: number; limit: number; total_page: number } },
    { search: string; page: number; limit: number },
    { rejectValue: string }
>(
    'GetallSmsCampaign',
    async ({ search, page, limit }, { rejectWithValue }) => {
        try {
            await delay(1000)
            const response = await axiosInstance.get(`api/admin/sms-campaigns`, {
                params: { search, page, limit },
            });
            return { data: response.data.data, info: response.data.info };
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error.message);
        }
    }
);
// ====================SmsCampgain-listing-action-start==============

// ==================AddSmsCampaigns-action-start===========
export const AddSmsCampaigns = createAsyncThunk<
    SmsCampaign,
    SmsCampaign,
    { rejectValue: string }
>(
    'smscampaigndata/AddSmsCampaigns',
    async (Adddata, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`api/admin/sms-campaigns/create`, Adddata);
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error.message);
        }
    }
);
// ==================AddSmsCampaigns-action-end===========

// =================UpdateSmsCampaigns-action-start=========
export const UpdateSmsCampaigns = createAsyncThunk<
    SmsCampaign,
    { id: number; updatedData: SmsCampaign },
    { rejectValue: string }
>(
    'smscampaigndata/UpdateSmsCampaigns',
    async ({ id, updatedData }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.put(`api/admin/sms-campaigns/update/${id}`, updatedData);
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error.message);
        }
    }
);
// =================UpdateSmsCampaigns-action-end=========


// ================DeleteSmsCampaign-action-start=========
export const DeleteSmsCampaign = createAsyncThunk<
    { id: number },
    { id: number }
>(
    'smtpgatewaysdata/DeleteSmsCampaign',
    async ({ id }) => {
        try {
            const { data } = await axiosInstance.delete(`api/admin/sms-campaigns/${id}`);
            return data;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return error.response.data;
            }
            return error.message;
        }
    }
);
// ================DeleteSmsCampaign-action-end=========


const initialState: SmsCampaignState = {
    smscampaigndata: [],
    info: null,
    error: null,
    loading: false,
};

const smscampaignSlice = createSlice({
    name: "Smscampaign",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // ===============get-all-smtp-slice-start============
            .addCase(GetallSmsCampaign.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(GetallSmsCampaign.fulfilled, (state, action: PayloadAction<{ data: SmsCampaign[], info: { count: number; current_page: number; limit: number; total_page: number } }>) => {
                state.loading = false;
                state.smscampaigndata = action.payload.data;
                state.info = action.payload.info;
            })
            .addCase(GetallSmsCampaign.rejected, (state, action: PayloadAction<string | undefined>) => {
                state.loading = false;
                state.error = action.payload || 'An error occurred';
            });
        // ===============get-all-smtp-slice-end============

        // =================add-=smtp-slice-start============
        builder
            .addCase(AddSmsCampaigns.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(AddSmsCampaigns.fulfilled, (state, action: PayloadAction<SmsCampaign>) => {
                state.loading = false;
                state.smscampaigndata.push(action.payload);
            })
            .addCase(
                AddSmsCampaigns.rejected, (state, action: PayloadAction<string | undefined>) => {
                    state.loading = false;
                    state.error = action.payload || 'Failed to add smscampaign';
                }
            );
        // =================add-=smtp-slice-end============

        // =================Update-smtp-slice-start============
        builder
            .addCase(UpdateSmsCampaigns.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(UpdateSmsCampaigns.fulfilled, (state, action: PayloadAction<SmsCampaign>) => {
                state.loading = false;
                const index = state.smscampaigndata.findIndex(item => item.id === action.payload.id);
                if (index !== -1) {
                    state.smscampaigndata[index] = action.payload;
                }
            })
            .addCase(UpdateSmsCampaigns.rejected, (state, action: PayloadAction<string | undefined>) => {
                state.loading = false;
                state.error = action.payload || 'Failed to update smscampaign';
            });
        // =================Update-smtp-slice-end============


        // =================Delete-smtp-slice-start============
        builder
            .addCase(DeleteSmsCampaign.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(DeleteSmsCampaign.fulfilled, (state, action: PayloadAction<{ id: number }>) => {
                state.loading = false;
            })
            .addCase(DeleteSmsCampaign.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload || 'Failed to delete smscampaign';
            });
        // =================Delete-smtp-slice-end============


    },
});

export default smscampaignSlice.reducer;
